import { openAIService } from '@/lib/ai/openai-service';
import { supabase } from '@/lib/supabase';

export interface Document {
  id: string;
  content: string;
  metadata?: Record<string, any>;
  embedding?: number[];
}

export class SupabaseVectorStore {
  private static instance: SupabaseVectorStore;
  private isInitialized = false;
  private useEmbeddings = false;
  private documentCount = 0;

  private constructor() {
    // Initialize and check if we can use embeddings
    this.useEmbeddings = openAIService.isReady();
    this.checkInitialization();
  }

  static getInstance(): SupabaseVectorStore {
    if (!SupabaseVectorStore.instance) {
      SupabaseVectorStore.instance = new SupabaseVectorStore();
    }
    return SupabaseVectorStore.instance;
  }

  /**
   * Check if the vector store has been initialized with documents
   */
  private async checkInitialization(): Promise<void> {
    try {
      const { count, error } = await supabase
        .from('document_embeddings')
        .select('*', { count: 'exact', head: true });
      
      if (error) {
        console.error('Error checking document count:', error);
        this.isInitialized = false;
        this.documentCount = 0;
        return;
      }
      
      this.documentCount = count || 0;
      this.isInitialized = this.documentCount > 0;
    } catch (error) {
      console.error('Error in checkInitialization:', error);
      this.isInitialized = false;
      this.documentCount = 0;
    }
  }

  /**
   * Add documents to the vector store
   */
  async addDocuments(documents: Omit<Document, 'embedding'>[]): Promise<void> {
    if (!this.useEmbeddings) {
      console.warn('OpenAI API key not configured. Embeddings will not be generated.');
      return;
    }
    
    try {
      for (const doc of documents) {
        // Generate embedding using OpenAI
        const embedding = await openAIService.createEmbedding(doc.content);
        
        // Store document with embedding in Supabase
        const { error } = await supabase
          .from('document_embeddings')
          .insert({
            content: doc.content,
            metadata: doc.metadata || {},
            embedding
          });
        
        if (error) {
          console.error('Error storing document:', error);
        }
      }
      
      // Update initialization status
      await this.checkInitialization();
    } catch (error) {
      console.error('Error adding documents:', error);
    }
  }

  /**
   * Perform a semantic search to find the most relevant documents
   */
  async similaritySearch(query: string, topK = 3): Promise<Document[]> {
    if (!this.isInitialized || !this.useEmbeddings) {
      return this.keywordSearch(query, topK);
    }

    try {
      // Generate embedding for the query
      const queryEmbedding = await openAIService.createEmbedding(query);
      
      // Search for similar documents using the match_documents function
      const { data, error } = await supabase.rpc(
        'match_documents',
        {
          query_embedding: queryEmbedding,
          match_threshold: 0.7,
          match_count: topK
        }
      );
      
      if (error) {
        console.error('Error in similarity search:', error);
        return this.keywordSearch(query, topK);
      }
      
      return data.map((item: { id: string; content: string; metadata: Record<string, any> }) => ({
        id: item.id,
        content: item.content,
        metadata: item.metadata
      }));
    } catch (error) {
      console.error('Error in similaritySearch:', error);
      return this.keywordSearch(query, topK);
    }
  }

  /**
   * Fallback keyword search when embeddings are not available
   */
  private async keywordSearch(query: string, topK = 3): Promise<Document[]> {
    try {
      const { data, error } = await supabase
        .from('document_embeddings')
        .select('id, content, metadata')
        .textSearch('content', query)
        .limit(topK);
      
      if (error) {
        console.error('Error in keyword search:', error);
        return [];
      }
      
      return data.map((item: { id: string; content: string; metadata: Record<string, any> }) => ({
        id: item.id,
        content: item.content,
        metadata: item.metadata
      }));
    } catch (error) {
      console.error('Error in keywordSearch:', error);
      return [];
    }
  }

  /**
   * Load training data from the given text
   */
  async loadTrainingData(text: string): Promise<void> {
    // Split text into chunks of roughly 1000 characters
    const chunkSize = 1000;
    const overlap = 200;
    const chunks: Omit<Document, 'embedding'>[] = [];
    
    for (let i = 0; i < text.length; i += chunkSize - overlap) {
      const chunk = text.slice(i, i + chunkSize);
      chunks.push({
        id: `chunk-${i}`,
        content: chunk,
        metadata: { position: i, source: 'training-data' }
      });
    }
    
    await this.addDocuments(chunks);
  }

  /**
   * Check if the vector store has been initialized with documents
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Get the number of documents in the store
   */
  getDocumentCount(): number {
    return this.documentCount;
  }

  /**
   * Remove a document from the store by ID
   */
  async removeDocument(id: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('document_embeddings')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error removing document:', error);
        return false;
      }
      
      // Update document count
      await this.checkInitialization();
      return true;
    } catch (error) {
      console.error('Error in removeDocument:', error);
      return false;
    }
  }

  /**
   * Get all documents in the store
   */
  async getAllDocuments(): Promise<Document[]> {
    try {
      const { data, error } = await supabase
        .from('document_embeddings')
        .select('id, content, metadata')
        .limit(100); // Limit to prevent loading too many documents
      
      if (error) {
        console.error('Error getting all documents:', error);
        return [];
      }
      
      return data.map((item: { id: string; content: string; metadata: Record<string, any> }) => ({
        id: item.id,
        content: item.content,
        metadata: item.metadata
      }));
    } catch (error) {
      console.error('Error in getAllDocuments:', error);
      return [];
    }
  }

  /**
   * Clear all documents from the store
   */
  async clearDocuments(): Promise<void> {
    try {
      const { error } = await supabase
        .from('document_embeddings')
        .delete()
        .neq('id', 'placeholder'); // Delete all documents
      
      if (error) {
        console.error('Error clearing documents:', error);
      }
      
      // Update initialization status
      await this.checkInitialization();
    } catch (error) {
      console.error('Error in clearDocuments:', error);
    }
  }

  /**
   * Check if we're using OpenAI embeddings
   */
  isUsingEmbeddings(): boolean {
    return this.useEmbeddings;
  }
}

export const supabaseVectorStore = SupabaseVectorStore.getInstance(); 